import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight } from "lucide-react";

import teamBadgePlaceholder from "../../../../assets/images/team_badge_placeholder.svg";

const ITEM_CLASS =
  "w-full text-left px-4 py-3 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors";
const ITEM_DESTRUCTIVE_CLASS =
  "w-full text-left px-4 py-3 text-sm font-medium text-red-400 hover:bg-red-500/20 hover:text-red-300 cursor-pointer transition-colors";
const CONTAINER_CLASS =
  "bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden";

const TeamSelectorDropdown = ({
  isOpen,
  teamId,
  progressionRuleText,
  availableTeams,
  onUnassignTeam,
  onRemoveRule,
  onAssignTeam,
  ruleSources = [],
  onAssignRule,
  dropDirection = "down",
  triggerRef,
}) => {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, bottom: 0, left: 0, width: 0, maxHeight: 300 });
  const [activeSubmenu, setActiveSubmenu] = useState(null); // "team" | "rule" | null
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0, maxHeight: 400 });

  // Reset submenu when dropdown closes
  useEffect(() => {
    if (!isOpen) setActiveSubmenu(null);
  }, [isOpen]);

  // Position the primary dropdown relative to trigger
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      setPosition({
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: Math.max(rect.width, 200),
        maxHeight: dropDirection === "up" ? Math.min(spaceAbove, 352) : Math.min(spaceBelow, 352),
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, triggerRef, dropDirection]);

  // Position the submenu next to the hovered primary item
  const handleSubmenuHover = (type, e) => {
    const itemRect = e.currentTarget.getBoundingClientRect();
    const submenuWidth = 250;
    const spaceRight = window.innerWidth - itemRect.right - 8;
    const openRight = spaceRight >= submenuWidth;

    setSubmenuPosition({
      top: itemRect.top,
      left: openRight ? itemRect.right + 4 : itemRect.left - submenuWidth - 4,
      maxHeight: Math.min(window.innerHeight - itemRect.top - 8, 400),
    });
    setActiveSubmenu(type);
  };

  if (!isOpen) return null;

  const isAssigned = !!teamId;
  const hasRule = !!progressionRuleText && !!onRemoveRule;
  const showTwoLevel = !isAssigned && !hasRule;

  const dropdown = (
    <div
      ref={dropdownRef}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseLeave={() => setActiveSubmenu(null)}
      style={{
        position: "fixed",
        top: dropDirection === "up" ? undefined : position.top,
        bottom: dropDirection === "up" ? position.bottom : undefined,
        left: position.left,
        width: position.width,
        zIndex: 999999,
        maxHeight: position.maxHeight,
      }}
      className={CONTAINER_CLASS}
    >
      {isAssigned ? (
        <button className={ITEM_DESTRUCTIVE_CLASS} onClick={onUnassignTeam}>
          Unassign Team
        </button>
      ) : hasRule ? (
        <button className={ITEM_DESTRUCTIVE_CLASS} onClick={onRemoveRule}>
          Remove Rule
        </button>
      ) : (
        <>
          {availableTeams.length > 0 && (
            <button
              className={`${ITEM_CLASS} flex items-center justify-between ${activeSubmenu === "team" ? "bg-[#0D1B29]" : ""}`}
              onMouseEnter={(e) => handleSubmenuHover("team", e)}
            >
              Assign team
              <ChevronRight size={14} className="text-[#7F8585]" />
            </button>
          )}
          {ruleSources.length > 0 && onAssignRule && (
            <button
              className={`${ITEM_CLASS} flex items-center justify-between ${activeSubmenu === "rule" ? "bg-[#0D1B29]" : ""}`}
              onMouseEnter={(e) => handleSubmenuHover("rule", e)}
            >
              Assign from rule
              <ChevronRight size={14} className="text-[#7F8585]" />
            </button>
          )}
          {availableTeams.length === 0 && ruleSources.length === 0 && (
            <div className="px-4 py-3 text-sm font-medium text-[#7F8585]">
              No options available
            </div>
          )}
        </>
      )}
    </div>
  );

  // Level 2: Submenu
  const submenu = showTwoLevel && activeSubmenu ? (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setActiveSubmenu(activeSubmenu)}
      onMouseLeave={() => setActiveSubmenu(null)}
      style={{
        position: "fixed",
        top: submenuPosition.top,
        left: submenuPosition.left,
        width: 250,
        zIndex: 999999,
        maxHeight: submenuPosition.maxHeight,
      }}
      className={`${CONTAINER_CLASS} overflow-y-auto`}
    >
      {activeSubmenu === "team" &&
        availableTeams.map((availableTeam) => (
          <button
            key={availableTeam._id}
            className={`${ITEM_CLASS} flex items-center gap-3`}
            onClick={() => onAssignTeam(availableTeam._id)}
          >
            <img
              src={availableTeam.teamId?.crest || availableTeam.teamCrest || teamBadgePlaceholder}
              alt={availableTeam.teamId?.title || availableTeam.teamTitle || "Team"}
              className="w-5 h-5 rounded-full object-cover shrink-0"
              onError={(e) => {
                e.target.src = teamBadgePlaceholder;
              }}
            />
            <span>{availableTeam.teamId?.title || availableTeam.teamTitle || "Unknown Team"}</span>
          </button>
        ))}

      {activeSubmenu === "rule" &&
        (() => {
          let lastGroup = null;
          return ruleSources.map((source, idx) => {
            const showGroupHeader = source.groupLabel !== lastGroup;
            lastGroup = source.groupLabel;
            return (
              <div key={idx}>
                {showGroupHeader && (
                  <div className="px-4 py-2 bg-app-surface border-b border-[#192634]">
                    <p className="text-app-text-secondary text-[11px] font-medium m-0">{source.groupLabel}</p>
                  </div>
                )}
                <button
                  className={`${ITEM_CLASS} pl-4`}
                  onClick={() => onAssignRule(source)}
                >
                  {source.label}
                </button>
              </div>
            );
          });
        })()}
    </div>
  ) : null;

  return createPortal(
    <>
      {dropdown}
      {submenu}
    </>,
    document.body
  );
};

export default TeamSelectorDropdown;
